import React, { useEffect, useState } from 'react'
import {
  Button,
  Col,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
  Form,
  FormGroup,
  FormFeedback
} from 'reactstrap'
import { ICategory } from './index'
import { toast } from 'react-toastify'
import { useFormik } from 'formik'
import * as Yup from 'yup'

interface IProps {
  categoryRecord: ICategory | undefined
  isOpen: boolean
  editMode: boolean
  setOpenPopup: (arg: boolean) => void
  handleCreateOrUpdateCategory: (arg: any) => Promise<string>
}

const initCategory: ICategory = {
  name: '',
  description: '',
  price: 0,
  isActive: true
}

const CategoryModal: React.FC<IProps> = ({
  categoryRecord,
  isOpen,
  editMode,
  handleCreateOrUpdateCategory,
  setOpenPopup
}) => {
  const [categoryState, setCategoryState] = useState<ICategory>(initCategory)
  const hasEdit = (editMode && categoryRecord) || false

  const formik: any = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: hasEdit ? categoryRecord?.name : '',
      description: hasEdit ? categoryRecord?.description : '',
      price: hasEdit ? categoryRecord?.price : 0
    },
    validationSchema: Yup.object({
      name: Yup.string().required('İsim zorunludur'),
      price: Yup.number().min(0, 'Fiyat negatif olamaz')
    }),
    onSubmit: () => {
      handleSubmit()
    }
  })

  useEffect(() => {
    if (editMode && categoryRecord) {
      setCategoryState(categoryRecord)
    } else {
      setCategoryState(initCategory)
    }

    if (isOpen) {
      formik.resetForm()
    }
  }, [isOpen, editMode, categoryRecord])

  const togglePopup = () => {
    setOpenPopup(!isOpen)
  }

  const handleOnChange = (e: { target: { name: string; value: string } }) => {
    const { name, value } = e.target
    setCategoryState({
      ...categoryState,
      [name]: name === 'price' ? parseFloat(value) || 0 : value
    })
    formik.setFieldValue(name, name === 'price' ? parseFloat(value) || 0 : value)
  }

  const handleActiveChange = (e: { target: { checked: boolean } }) => {
    setCategoryState({
      ...categoryState,
      isActive: e.target.checked
    })
  }

  const handleSubmit = async () => {
    const payload = {
      ...categoryState,
      _id: editMode ? categoryRecord?._id : undefined
    }

    const message = await handleCreateOrUpdateCategory(payload)
    if (message === 'SUCCESSFULL') {
      toast.success(
        editMode ? 'Kategori başarıyla güncellendi' : 'Yeni kategori başarıyla oluşturuldu'
      )
      setOpenPopup(false)
    } else {
      toast.error(message)
    }
  }

  return (
    <React.Fragment>
      <Modal
        isOpen={isOpen}
        toggle={togglePopup}
        scrollable={true}
        id='categoryModal'
      >
        <ModalHeader className='modal-title' id='categoryModalTitle' toggle={togglePopup}>
          {editMode ? 'Kategori Düzenle' : 'Yeni Kategori Oluştur'}
        </ModalHeader>
        <ModalBody>
          <Form
            className='needs-validation'
            onSubmit={(e) => {
              e.preventDefault()
              formik.handleSubmit()
              return false
            }}
          >
            <Row>
              <FormGroup>
                <Col md={12} className='mb-3'>
                  <Label htmlFor='name' className='form-label mb-1 fw-semibold'>
                    İsim
                  </Label>
                  <Input
                    type='text'
                    className='form-control'
                    id='name'
                    name='name'
                    placeholder='Kategori ismi'
                    value={categoryState?.name}
                    onChange={handleOnChange}
                    invalid={formik.touched.name && formik.errors.name ? true : false}
                  />
                  {formik.touched.name && formik.errors.name ? (
                    <FormFeedback type='invalid'>{formik.errors.name}</FormFeedback>
                  ) : null}
                </Col>
              </FormGroup>

              <FormGroup>
                <Col md={12} className='mb-3'>
                  <Label htmlFor='description' className='form-label mb-1 fw-semibold'>
                    Açıklama
                  </Label>
                  <Input
                    type='textarea'
                    className='form-control'
                    id='description'
                    name='description'
                    placeholder='Kategori açıklaması'
                    rows={3}
                    value={categoryState?.description}
                    onChange={handleOnChange}
                  />
                </Col>
              </FormGroup>

              <FormGroup>
                <Col md={12} className='mb-3'>
                  <Label htmlFor='price' className='form-label mb-1 fw-semibold'>
                    Fiyat (TL)
                  </Label>
                  <Input
                    type='number'
                    className='form-control'
                    id='price'
                    name='price'
                    placeholder='0'
                    min={0}
                    step={0.01}
                    value={categoryState?.price}
                    onChange={handleOnChange}
                    invalid={formik.touched.price && formik.errors.price ? true : false}
                  />
                  {formik.touched.price && formik.errors.price ? (
                    <FormFeedback type='invalid'>{formik.errors.price}</FormFeedback>
                  ) : null}
                </Col>
              </FormGroup>

              <FormGroup>
                <Col md={12} className='mb-3'>
                  <div className='form-check form-switch'>
                    <Input
                      type='checkbox'
                      className='form-check-input'
                      id='isActive'
                      checked={categoryState?.isActive}
                      onChange={handleActiveChange}
                    />
                    <Label className='form-check-label' htmlFor='isActive'>
                      Aktif
                    </Label>
                  </div>
                </Col>
              </FormGroup>
            </Row>

            <div className='modal-footer'>
              <Button color='light' onClick={() => setOpenPopup(false)}>
                Kapat
              </Button>
              <Button color='primary' type='submit'>
                Kaydet
              </Button>
            </div>
          </Form>
        </ModalBody>
      </Modal>
    </React.Fragment>
  )
}

export default CategoryModal
